import type { CityConfig } from "../city-template/types";

export const costaMesaConfig: CityConfig = {
  city: "Costa Mesa",
  state: "California",
  stateAbbr: "CA",
  slug: "costa-mesa",
  route: "/cities/costa-mesa",
  title: "Emergency Passport Costa Mesa, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Costa Mesa, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Central Orange County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Costa Mesa, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Costa Mesa and Central Orange County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Costa%20Mesa%20California%20cityscape%20at%20golden%20hour%20with%20modern%20SoCo%20shopping%20district%20and%20South%20Coast%20Plaza%20area%2C%20contemporary%20architecture%20with%20warm%20sunset%20light%2C%20palm%20trees%20and%20manicured%20streets%2C%20professional%20cityscape%20photography%2C%20clean%20urban%20environment%20with%20suburban%20charm%2C%20no%20visible%20text%20or%20people%2C%20warm%20amber%20and%20coral%20tones&width=1800&height=700&seq=costa-mesa-hero-001&orientation=landscape",
  heroImageAlt: "Costa Mesa California cityscape at golden hour with modern South Coast area and contemporary architecture",
  geoPosition: "33.6631;-117.9124",
  geoPlacename: "Costa Mesa, California",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "300 N Los Angeles St, Los Angeles, CA 90012",
    distance: "Located in downtown LA — approximately 45–60 minutes from Costa Mesa via I-405",
  },
  nearbyCitiesZips: [
    { name: "Costa Mesa", zips: "92626, 92627" },
    { name: "Newport Beach", zips: "92660, 92661, 92663" },
    { name: "Huntington Beach", zips: "92605, 92646–92649" },
    { name: "Fountain Valley", zips: "92708, 92728" },
    { name: "Irvine", zips: "92602–92604, 92606, 92612, 92614, 92617–92620" },
    { name: "Santa Ana", zips: "92701–92707" },
    { name: "Anaheim", zips: "92801–92808" },
    { name: "Garden Grove", zips: "92840–92846" },
    { name: "Orange", zips: "92856, 92861, 92864, 92866, 92867, 92868, 92869" },
    { name: "Tustin", zips: "92780, 92782" },
    { name: "Laguna Beach", zips: "92651, 92652" },
    { name: "Laguna Hills", zips: "92653, 92654, 92656" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Central Orange County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "San Diego", route: "/cities/san-diego" },
    { name: "Irvine", route: "/cities/irvine" },
    { name: "Santa Ana", route: "/cities/santa-ana" },
    { name: "Anaheim", route: "/cities/anaheim" },
    { name: "Huntington Beach", route: "/cities/huntington-beach" },
    { name: "Newport Beach", route: "/cities/newport-beach" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Costa Mesa remotely via secure courier. We do not have a walk-in office in Costa Mesa — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Central Orange County travelers.",
  fpcOfficeAddress: "200 Spectrum Center Dr, Irvine, CA 92618",
  fpcOfficeDistance: "Serving all of Central Orange County",
  serviceAreaIntro: "From the South Coast Plaza to the SoCo Collection, from the west side to the east side — Fast Passport Center serves the entire Costa Mesa community. We regularly help travelers from Newport Beach, Huntington Beach, Fountain Valley, and Irvine get their passports fast.",
  nearbyCitiesIntro: "We serve all Costa Mesa neighborhoods and nearby Central Orange County cities including Newport Beach, Huntington Beach, Fountain Valley, Irvine, and Santa Ana. If you are in central Orange County, we have you covered.",
  howItWorksIntro: "Most Costa Mesa travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With John Wayne Airport (SNA) just 10 minutes away and LAX within reach, Costa Mesa travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Costa Mesa and Central Orange County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Costa Mesa? Here are the most common ones we hear from Central Orange County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Costa Mesa international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Robert H.",
      location: "Costa Mesa, CA",
      rating: 5,
      text: "My business trip to Hong Kong was in 2 days and my passport was expired. Fast Passport Center got me a new one in 30 hours. Saved a major deal.",
      service: "Emergency Passport",
    },
    {
      name: "Lisa M.",
      location: "Newport Beach, CA",
      rating: 5,
      text: "Needed to renew for a family cruise to Alaska. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Kevin T.",
      location: "Fountain Valley, CA",
      rating: 5,
      text: "Lost my passport a week before my wedding in Italy. They walked me through the DS-64 process and got me a replacement in 48 hours. Saved our big day.",
      service: "Lost Passport",
    },
    {
      name: "Amanda C.",
      location: "Irvine, CA",
      rating: 5,
      text: "As a frequent international business traveler from Orange County, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "costa-mesa-1",
      name: "Costa Mesa Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1860 Newport Blvd",
      city: "Costa Mesa",
      state: "CA",
      zip: "92627",
      phone: "(949) 646-5314",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from South Coast Plaza",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "costa-mesa-2",
      name: "Newport Beach Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1860 San Joaquin Hills Rd",
      city: "Newport Beach",
      state: "CA",
      zip: "92660",
      phone: "(949) 644-3249",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from Fashion Island",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "costa-mesa-3",
      name: "Huntington Beach Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6771 Warner Ave",
      city: "Huntington Beach",
      state: "CA",
      zip: "92647",
      phone: "(714) 842-6367",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3.5 miles from Huntington Beach Pier",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for north OC residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "costa-mesa-4",
      name: "Santa Ana Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3101 W Sunflower Ave",
      city: "Santa Ana",
      state: "CA",
      zip: "92704",
      phone: "(714) 545-2601",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.2 miles from downtown Santa Ana",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Orange County. Book appointments ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "costa-mesa-5",
      name: "Fountain Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "11702 Slater Ave",
      city: "Fountain Valley",
      state: "CA",
      zip: "92708",
      phone: "(714) 775-3294",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from Fountain Valley Town Center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for central OC residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "costa-mesa-6",
      name: "Orange County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "333 W Santa Ana Blvd",
      city: "Santa Ana",
      state: "CA",
      zip: "92701",
      phone: "(714) 834-2500",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from Santa Ana City Hall",
      website: "https://www.ocassessor.com/",
      websiteLabel: "OC Clerk-Recorder",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Central OC photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Orange County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Costa Mesa applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Costa Mesa families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Costa Mesa travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Costa Mesa." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Costa Mesa address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Costa Mesa before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Costa Mesa for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most Orange County travelers." },
    { q: "How fast can I renew my passport in Costa Mesa?", a: "Fast Passport Center offers fast passport renewal in Costa Mesa in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Costa Mesa in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Costa Mesa?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Costa Mesa residents." },
    { q: "Can I expedite a child passport in Costa Mesa?", a: "Yes. Fast Passport Center expedites child passports in Costa Mesa for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Costa Mesa with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Costa Mesa?",
      a: "With Fast Passport Center's emergency courier service, Costa Mesa travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Costa Mesa?",
      a: "Popular options include the Costa Mesa Main Post Office (92627), Newport Beach Post Office (92660), and Huntington Beach Post Office (92647). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Costa Mesa?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Newport Beach or Laguna Beach?",
      a: "We serve all of Orange County coastal communities including Newport Beach, Laguna Beach, Dana Point, and San Clemente. Our overnight shipping labels work from any UPS location, so your location within the coastal area doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Costa Mesa?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Costa Mesa?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Costa Mesa and Newport Beach offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Costa Mesa?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Costa Mesa?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Orange County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Costa Mesa", address: "3030 Harbor Blvd, Costa Mesa, CA 92626", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Newport Beach", address: "1700 San Joaquin Hills Rd, Newport Beach, CA 92660", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Costa Mesa", address: "2701 Harbor Blvd, Costa Mesa, CA 92626", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Huntington Beach", address: "16511 Bolsa Chica St, Huntington Beach, CA 92649", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Santa Ana", address: "3600 W McFadden Ave, Santa Ana, CA 92704", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Fountain Valley", address: "18020 Brookhurst St, Fountain Valley, CA 92708", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Costa Mesa", address: "2937 Harbor Blvd, Costa Mesa, CA 92626", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Fountain Valley", address: "17900 Newhope St, Fountain Valley, CA 92708", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Costa Mesa",
      address: "2269 W 19th St, Costa Mesa, CA 92627",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Santa Ana",
      address: "1330 E First St, Santa Ana, CA 92701",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Huntington Beach",
      address: "16780 Goldenwest St, Huntington Beach, CA 92647",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Fullerton",
      address: "2525 W Orangethorpe Ave, Fullerton, CA 92833",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for northern Orange County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SNA",
      name: "John Wayne Airport (Orange County)",
      distance: "8 miles from Costa Mesa",
      address: "18601 Airport Way, Santa Ana, CA 92707",
      note: "The primary airport for Orange County. International flights to Mexico, Canada, and connections to Europe and Asia.",
      flightUrl: "https://www.ocair.com/",
      flightLabel: "John Wayne Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "40 miles from Costa Mesa",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-405.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "LGB",
      name: "Long Beach Airport",
      distance: "20 miles from Costa Mesa",
      address: "4100 Donald Douglas Dr, Long Beach, CA 90808",
      note: "Convenient for domestic flights and some international connections. Less crowded than LAX.",
      flightUrl: "https://www.longbeach.gov/lgb/",
      flightLabel: "Long Beach Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [{ icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from California CDPH Vital Records for Orange County.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" }, { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through California CDPH Vital Records.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" }, { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Orange County Clerk-Recorder.", url: "https://www.occr.ocgov.com/" }, { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through Orange County Superior Court.", url: "https://www.occourts.org/" }],
};